import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { MultiCheckboxField } from '@/components/form/checkbox/MultiCheckbox';
import { TsForm } from '@/components/form/Form';
import { GridSelectField } from '@/components/form/GridSelectInput';
import { SelectInputField, SelectOption } from '@/components/form/SelectInput';
import { SubmitCancelButtons } from '@/components/form/SubmitCancelButtons';
import { TextField } from '@/components/form/TextInput';
import { GridDropdownType, GridSelectOption } from '@/components/grid/GridSelect';
import { TSTitleCard } from '@/components/shared/card/Card';
import { colorArray, getIconArrayByCategory, standardIconArray } from '@/helpers/dropdownOptionHelper';
import { handleMutationResult } from '@/helpers/toastHelper';
import { IconCategory, TaskType, TextColors } from '@/services/tasks/task.types';
import { useActiveSkills } from '@/services/tasks/useSkills';
import { useTasks, useUpdateTasks } from '@/services/tasks/useTasks';
import { strings } from '@/services/translation/strings';
import clsx from 'clsx';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { IconType } from 'react-icons';
import { FiClipboard } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import adminStrings from '../../admin.translations';

interface AdminEditTaskDetailsCardProps {
  task: TaskType;
}

interface EditTaskFormValues {
  name: string;
  displayName: string;
  iconId: string;
  iconBgColourId: string;
  iconTextColour: TextColors;
  requiredSkillIds: string[];
}

export const textColorOptions: SelectOption[] = [
  { name: strings.colors.black, value: TextColors.BLACK },
  { name: strings.colors.white, value: TextColors.WHITE }
];

const ICON_TABS = [
  { key: IconCategory.standard, label: strings.settings.manageTasks.iconSections.standard },
  { key: IconCategory.retail, label: strings.settings.manageTasks.iconSections.retail },
  { key: IconCategory.hospitality, label: strings.settings.manageTasks.iconSections.hospitality },
  { key: IconCategory.numbers, label: strings.settings.manageTasks.iconSections.numbers }
];

const AdminEditTaskDetailsCard: FunctionComponent<AdminEditTaskDetailsCardProps> = ({ task }) => {
  const navigate = useNavigate();

  const bgColourLookup = useMemo(() => colorArray.find(o => o.value === task.color), [task]);

  const methods = useForm<EditTaskFormValues>({
    defaultValues: {
      name: task.name,
      displayName: task.displayName,
      iconId: task.icon?.icon ?? standardIconArray[0].id,
      iconBgColourId: bgColourLookup ? bgColourLookup.id : colorArray[0].id,
      iconTextColour: task.textColor,
      requiredSkillIds: task.skills
    }
  });

  useEffect(() => {
    methods.reset({
      name: task.name,
      displayName: task.displayName,
      iconId: task.icon?.icon ?? standardIconArray[0].id,
      iconBgColourId: bgColourLookup ? bgColourLookup.id : colorArray[0].id,
      iconTextColour: task.textColor,
      requiredSkillIds: task.skills
    });
  }, [task]);

  const { fetching, update } = useUpdateTasks();

  const tasks = useTasks();

  const onSubmit = async (values: EditTaskFormValues) => {
    const bgColourValue = colorArray.find(o => o.id === values.iconBgColourId)?.value;

    const result = await update([
      {
        ...task,
        name: values.name,
        displayName: values.displayName,
        icon: { icon: values.iconId, category: activeIconCategory },
        color: bgColourValue,
        textColor: values.iconTextColour,
        skills: values.requiredSkillIds
      }
    ]);

    await handleMutationResult(result, {
      successMessage: adminStrings.tasksSection.edit.successMessage(values.name),
      errorMessage: adminStrings.tasksSection.edit.errorMessage(task.name),
      customSuccessCheck: data => !!data?.updateTasks,
      onSuccess: () => {
        handleClose();
      }
    });
  };
  const handleClose = () => {
    methods.reset();
    navigate('/admin/tasks');
  };

  const [activeIconCategory, setActiveIconTabCategory] = useState(task.icon?.category ?? IconCategory.standard);

  const iconArray = useMemo(() => {
    return getIconArrayByCategory(activeIconCategory);
  }, [activeIconCategory]);

  const onTabChange = (tabKey: string) => {
    setActiveIconTabCategory(tabKey as IconCategory);
  };

  const skills = useActiveSkills();

  const skillOptions = useMemo(() => {
    return skills.map(s => {
      return {
        id: s.id,
        value: false,
        suffix: s.displayName,
        onChange: () => {}
      };
    });
  }, [skills]);

  const watchedIconId = useWatch({ control: methods.control, name: 'iconId' });
  const watchedBgColourId = useWatch({ control: methods.control, name: 'iconBgColourId' });
  const watchedTextColour = useWatch({ control: methods.control, name: 'iconTextColour' });

  const PreviewIcon = useMemo(() => {
    const option = iconArray.find(o => o.id === watchedIconId) ?? standardIconArray.find(o => o.id === watchedIconId);
    return option?.value as IconType | undefined;
  }, [watchedIconId, iconArray]);

  const previewBgColour = useMemo(() => {
    return colorArray.find(o => o.id === watchedBgColourId)?.value;
  }, [watchedBgColourId]);

  return (
    <TSTitleCard title={adminStrings.tasksSection.edit.title(task.name)} icon={FiClipboard} className="w-full">
      <SubmissionErrorBoundary>
        <TsForm methods={methods} onSubmit={onSubmit}>
          <div className="flex flex-col space-y-3 w-full">
            <TextField
              label={adminStrings.tasksSection.edit.taskName}
              name="name"
              id="name"
              placeholder={adminStrings.tasksSection.edit.taskName}
              customValidatorMessage={methods.formState.errors.name?.message}
              required
              rules={{
                validate: (value: string) => {
                  const isDuplicate = tasks?.some(t => t?.name === value && t?.id !== task.id);
                  if (isDuplicate) return adminStrings.tasksSection.edit.uniqueName;
                  return true;
                }
              }}
            />
            <TextField
              label={adminStrings.tasksSection.edit.taskDisplayName}
              name="displayName"
              id="displayName"
              placeholder={adminStrings.tasksSection.edit.taskName}
              customValidatorMessage={methods.formState.errors.name?.message}
              required
              rules={{
                validate: (value: string) => {
                  const isDuplicate = tasks?.some(t => t?.displayName === value && t?.id !== task.id);
                  if (isDuplicate) return adminStrings.tasksSection.edit.uniqueName;
                  return true;
                }
              }}
            />
            <div className="flex flex-row w-full space-x-7">
              <GridSelectField
                name="iconId"
                label={adminStrings.tasksSection.edit.taskIcon}
                id="iconId"
                itemsPerRow={8}
                type={GridDropdownType.ICON}
                dropdownOptions={standardIconArray}
                dropdownId="icon"
                className="w-24"
                tabs={{
                  headers: ICON_TABS,
                  activeHeader: activeIconCategory,
                  onTabChange: onTabChange
                }}
              >
                {iconArray.map((option, index) => (
                  <GridSelectOption key={index} option={option} type={GridDropdownType.ICON} />
                ))}
              </GridSelectField>

              <GridSelectField
                id="iconBgColourId"
                name="iconBgColourId"
                label={adminStrings.tasksSection.edit.backgroundColour}
                type={GridDropdownType.COLOR}
                dropdownOptions={colorArray}
                itemsPerRow={6}
                dropdownId={'backgroundColour'}
              >
                {colorArray.map((color, index) => (
                  <GridSelectOption
                    key={index}
                    className={clsx('p-1.5 text-2xl w-8 h-8 rounded-full')}
                    option={color}
                    type={GridDropdownType.COLOR}
                  />
                ))}
              </GridSelectField>

              <SelectInputField
                label={adminStrings.tasksSection.edit.textColour}
                options={textColorOptions}
                name={'iconTextColour'}
                id={'iconTextColour'}
              />

              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium">{adminStrings.tasksSection.edit.preview}</span>
                <div
                  className="w-11 h-11 flex items-center justify-center rounded-md"
                  style={{
                    background: previewBgColour,
                    color: watchedTextColour
                  }}
                >
                  {PreviewIcon ? <PreviewIcon className="h-6 w-6" /> : <div className="h-6 w-6" />}
                </div>
              </div>
            </div>
            <div>
              <MultiCheckboxField
                description={adminStrings.tasksSection.edit.requiredSkillsDescription}
                checkboxes={skillOptions}
                id={'requiredSkillIds'}
                inputClassName="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2"
                name={'requiredSkillIds'}
                label={adminStrings.tasksSection.edit.requiredSkills}
              />
            </div>
            <div className="flex items-center justify-end gap-3">
              <SubmitCancelButtons
                disabled={!!methods.formState.errors.name?.message}
                onComplete={handleClose}
                loading={fetching}
                submitText={strings.common.save}
              />
            </div>
          </div>
        </TsForm>
      </SubmissionErrorBoundary>
    </TSTitleCard>
  );
};

export default AdminEditTaskDetailsCard;
